import * as React from "react";
import { Button, Chip, Input, Label, Switch, cn } from "@moanhot/ui";
import { useAdminApi, type AdminEffect, type AdminLocation } from "@/api/adminClient";
import { TD, TH, when } from "@/screens/format";
import { useAdminAction, useAdminLoad } from "@/screens/hooks";
import { EmptyNote, LoadState, Page, Problem, Table } from "@/screens/ui";

function state(e: AdminEffect): { label: string; tone: string } {
  if (e.retires_at) return { label: "Retired", tone: "text-ink-3" };
  if (!e.b2b_enabled) return { label: "Off", tone: "text-ink-2" };
  if (!e.published_at) return { label: "Not published", tone: "text-warn" };
  return { label: "Published", tone: "text-ok" };
}

/** One storyboard's locations: the current preview, and a new mp4 plus poster. */
function Locations({ slug }: { slug: string }) {
  const api = useAdminApi();
  const [load, reload] = useAdminLoad((a) => a.listLocations(slug), [slug], "Could not load the locations.");
  const upload = useAdminAction();
  const [notice, setNotice] = React.useState<string | null>(null);

  const row = (loc: AdminLocation) => (
    <tr key={loc.key}>
      <td className={TD}>
        {loc.name}
        <div className="font-mono text-xs text-ink-3">{loc.key}</div>
      </td>
      <td className={TD}>
        {loc.poster_url ? <img src={loc.poster_url} alt="" className="h-16 w-auto rounded border border-line" /> : <span className="text-xs text-ink-3">No preview</span>}
      </td>
      <td className={TD}>
        <form
          className="flex flex-wrap items-end gap-3"
          onSubmit={(ev) => {
            ev.preventDefault();
            const file = (name: string) => (ev.currentTarget.elements.namedItem(name) as HTMLInputElement | null)?.files?.[0];
            const video = file("video");
            const poster = file("poster");
            if (!video || !poster)
              return upload.setProblem(`Pick both the MP4 and the poster for ${loc.name}.`);
            upload.run(api.uploadLocationPreview(slug, loc.key, video, poster), () => {
              setNotice(`${loc.name} preview uploaded.`);
              reload();
            });
          }}
        >
          <div className="space-y-1.5">
            <Label htmlFor={`video-${loc.key}`}>Preview MP4</Label>
            <Input id={`video-${loc.key}`} name="video" type="file" accept="video/mp4" />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor={`poster-${loc.key}`}>Poster</Label>
            <Input id={`poster-${loc.key}`} name="poster" type="file" accept="image/jpeg,image/png,image/webp" />
          </div>
          <Button type="submit" disabled={upload.busy}>
            Upload {loc.name}
          </Button>
        </form>
      </td>
    </tr>
  );

  return (
    <section aria-label={`Locations of ${slug}`} className="mt-6 rounded-lg border border-line p-5">
      <h2 className="mb-3 text-sm font-medium">Locations of <span className="font-mono">{slug}</span></h2>
      {notice && (
        <p role="status" className="mb-3 text-sm text-ink-2">
          {notice}
        </p>
      )}
      <Problem text={upload.problem} />
      <LoadState load={load} empty={(d) => (d.data.length === 0 ? <EmptyNote title="No locations" body="This storyboard has no enabled locations in the imported bundle." /> : null)}>
        {(d) => (
          <Table label={`Locations of ${slug}`} head={<><th className={TH}>Location</th><th className={TH}>Poster</th><th className={TH}>New preview</th></>}>
            {d.data.map(row)}
          </Table>
        )}
      </LoadState>
    </section>
  );
}

/** Imported effects: B2B on or off, publishing with a date, storyboard location previews. */
export function Catalogue() {
  const api = useAdminApi();
  const [load, reload] = useAdminLoad(
    (a) => a.listEffects(),
    [],
    "Could not load the catalogue.",
  );
  const [picked, setPicked] = React.useState<Set<string>>(new Set());
  const [notice, setNotice] = React.useState<string | null>(null);
  const toggle = useAdminAction();
  const publish = useAdminAction();
  const [locationsOf, setLocationsOf] = React.useState<string | null>(null);

  const flip = (slug: string) =>
    setPicked((s) => {
      const next = new Set(s);
      if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });

  return (
    <Page title="Catalogue">
      {notice && (
        <p role="status" className="mb-4 text-sm text-ink-2">
          {notice}
        </p>
      )}
      <Problem text={toggle.problem} />
      <LoadState load={load} empty={(d) => (d.data.length === 0 ? <EmptyNote title="No effects yet" body="Import a catalogue bundle to see its effects here." /> : null)}>
        {(d) => (
          <>
            <Table
              label="Effects"
              head={
                <>
                  <th className={TH}>
                    <span className="sr-only">Pick</span>
                  </th>
                  <th className={TH}>Effect</th>
                  <th className={TH}>Type</th>
                  <th className={TH}>State</th>
                  <th className={TH}>Published</th>
                  <th className={TH}>B2B</th>
                </>
              }
            >
              {d.data.map((e) => {
                const s = state(e);
                return (
                  <tr key={e.slug}>
                    <td className={TD}>
                      <input type="checkbox" aria-label={`Pick ${e.slug}`} checked={picked.has(e.slug)} disabled={e.retires_at !== null} onChange={() => flip(e.slug)} />
                    </td>
                    <td className={TD}>
                      {e.name}
                      <div className="font-mono text-xs text-ink-3">{e.slug}</div>
                    </td>
                    <td className={TD}>
                      {e.type} <span className="text-ink-3">{e.variant_count} variants</span>
                      {e.type === "storyboard" && (
                        <div>
                          <Button variant="ghost" size="sm" aria-expanded={locationsOf === e.slug} onClick={() => setLocationsOf(locationsOf === e.slug ? null : e.slug)}>
                            {locationsOf === e.slug ? "Hide locations" : "Locations"}
                          </Button>
                        </div>
                      )}
                    </td>
                    <td className={TD}>
                      <Chip className={s.tone}>{s.label}</Chip>
                    </td>
                    <td className={cn(TD, "text-xs")}>{e.published_at ? when(e.published_at) : ""}</td>
                    <td className={TD}>
                      <Switch
                        aria-label={`B2B ${e.slug}`}
                        checked={e.b2b_enabled}
                        disabled={toggle.busy}
                        onCheckedChange={(on) =>
                          toggle.run(api.toggleEffect(e.slug, on), (row) => {
                            setNotice(`${row.slug} is ${row.b2b_enabled ? "on" : "off"} for partners.`);
                            reload();
                          })
                        }
                      />
                    </td>
                  </tr>
                );
              })}
            </Table>
            {locationsOf && <Locations slug={locationsOf} />}
            <form
              className="mt-6 flex flex-wrap items-end gap-3 rounded-lg border border-line p-5"
              onSubmit={(ev) => {
                ev.preventDefault();
                const date = String(new FormData(ev.currentTarget).get("published_at") ?? "");
                if (picked.size === 0) return publish.setProblem("Pick the effects to publish.");
                if (!date) return publish.setProblem("Pick the publish date (UTC).");
                publish.run(api.publishEffects([...picked], `${date}:00Z`), (res) => {
                  setPicked(new Set());
                  setNotice(`${res.data.length} effects published.`);
                  reload();
                });
              }}
            >
              <div className="space-y-1.5">
                <Label htmlFor="pub-date">Publish on (UTC)</Label>
                <Input id="pub-date" name="published_at" type="datetime-local" className="font-mono" />
              </div>
              <Button type="submit" variant="primary" disabled={publish.busy}>
                Publish {picked.size || ""} picked
              </Button>
              <div className="basis-full">
                <Problem text={publish.problem} />
              </div>
            </form>
          </>
        )}
      </LoadState>
    </Page>
  );
}
